/**
 * payday.js — the ONE place a weekday becomes a `tenants.payday_period`, and back.
 *
 * **The problem this exists to remove.** Three vocabularies describe the same day, and no two of
 * them agree:
 *
 * | where | what it holds | order |
 * | --- | --- | --- |
 * | `Onboarding.jsx:828` (`DayPicker`) | a NUMBER 0-6 | Sunday = 0, the JS `Date.getDay()` convention |
 * | `Settings.jsx:244` | a day NAME string | **Monday first** |
 * | `tenants.payday_period` | `weekly_<lowercase day name>` | Sunday = 0, via `payday_weekday()` |
 *
 * `DayPicker` carries `weekStartsOn={1}`, which rotates the PILLS for display and leaves the
 * emitted key alone — `app/src/components/DayPicker.jsx:78-80` declares the keys as
 * `Date.getDay()`, and `buildDays` at `:117` only reorders them. So onboarding already speaks
 * PostgreSQL `dow`. Settings does not, and its list starts on Monday.
 *
 * **The bug this prevents.** `SETTINGS_OPTIONS.indexOf('Monday')` is 0, and 0 in the database is
 * SUNDAY. A family picks Monday, the screen shows Monday, and `guard_payout` settles up on Sunday.
 * Nothing throws and nothing logs. Every mapping below is therefore an explicit literal — there is
 * no arithmetic on a display order anywhere in this file, and there must never be.
 *
 * **`calendar_month` has no weekday**, and pretending otherwise is the second way to be silently
 * wrong. Both inverse functions answer `null` for it. `null` is not "unknown": it is "this family
 * settles up monthly, so a weekday is the wrong question". A caller must handle it. Returning 0
 * would move a monthly family to Sunday, and throwing would break their settings screen on load.
 *
 * **`ad_hoc` has no weekday either** (#580 I8a). A parent pays whenever they choose; its accounting
 * weeks run Monday to Monday, but that is bookkeeping, not a payday, and it has no next payday at
 * all. Both inverse functions answer `null` for it too, and `paydayKind` is how a caller tells the
 * two `null`s apart.
 *
 * **Unknown input throws.** There is deliberately no default. `Number('')` and `Number(null)` are
 * both `0`, so a permissive parse here writes Sunday into a family's account for an input that
 * meant nothing at all.
 */

/**
 * Every value `tenants_payday_period_check` allows. Keep in step with the F-30 migration and #580's
 * `1790431088158`, which added `ad_hoc`. This is the READ vocabulary: the server does not yet let a
 * family choose `ad_hoc`, but a screen must not break on a family that has it.
 */
export const PAYDAY_PERIODS = Object.freeze([
  'weekly_sunday',
  'weekly_monday',
  'weekly_tuesday',
  'weekly_wednesday',
  'weekly_thursday',
  'weekly_friday',
  'weekly_saturday',
  'calendar_month',
  'ad_hoc',
]);

/**
 * The seven day names a settings select shows, MONDAY first, because that is the order the Lantern
 * settings screen draws them in.
 *
 * Exported so there is exactly ONE written-out week in the application. `Settings.jsx` kept its own
 * copy until 2026-09-09, which is precisely the duplication this module exists to remove: two weeks
 * drift, and when they drift a family is paid on the wrong day. The ORDER here is display only —
 * every mapping in this file is by name or by `Date.getDay()` index, never by position in this
 * array.
 */
export const WEEKDAY_NAMES_MONDAY_FIRST = Object.freeze([
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
]);

/** The period a family settles up on once a month. It has no weekday. */
export const CALENDAR_MONTH = 'calendar_month';

/** The period of a family that pays whenever a parent chooses. No weekday and no next payday. */
export const AD_HOC = 'ad_hoc';

/**
 * Indexed by PostgreSQL `dow` / JS `Date.getDay()`: 0 is Sunday.
 *
 * This array IS the contract with `payday_weekday(period text)` in
 * `migrations/1788929031127_f30-payday-any-weekday.sql:25-35`. Read them side by side before
 * changing either.
 */
const PERIOD_BY_DOW = Object.freeze([
  'weekly_sunday',
  'weekly_monday',
  'weekly_tuesday',
  'weekly_wednesday',
  'weekly_thursday',
  'weekly_friday',
  'weekly_saturday',
]);

/**
 * Day name to period, written out one pair at a time.
 *
 * An object literal, NOT a lookup into any ordered week. The Settings screen's own list starts on
 * Monday, so any index arithmetic against it is off by one for every single day.
 */
const PERIOD_BY_NAME = Object.freeze({
  sunday: 'weekly_sunday',
  monday: 'weekly_monday',
  tuesday: 'weekly_tuesday',
  wednesday: 'weekly_wednesday',
  thursday: 'weekly_thursday',
  friday: 'weekly_friday',
  saturday: 'weekly_saturday',
});

/** Period back to the capitalised name the Settings select shows. */
const NAME_BY_PERIOD = Object.freeze({
  weekly_sunday: 'Sunday',
  weekly_monday: 'Monday',
  weekly_tuesday: 'Tuesday',
  weekly_wednesday: 'Wednesday',
  weekly_thursday: 'Thursday',
  weekly_friday: 'Friday',
  weekly_saturday: 'Saturday',
});

/** One error shape, so a caller can tell a mapping fault from a network fault. */
function reject(what, value) {
  const shown = typeof value === 'string' ? `"${value}"` : String(value);
  throw new TypeError(`payday: ${what} ${shown}`);
}

/**
 * A `DayPicker` value to the period the database stores.
 *
 * @param {number} dow 0 (Sunday) through 6 (Saturday), exactly as `DayPicker` emits it.
 * @returns {string} one of the seven `weekly_*` periods.
 * @throws {TypeError} for anything that is not an integer 0-6. A string `'3'` is refused too: it
 *   means some caller lost the type on the way here, and guessing hides that.
 */
export function paydayPeriodFromDayNumber(dow) {
  if (typeof dow !== 'number' || !Number.isInteger(dow) || dow < 0 || dow > 6) {
    reject('a weekday number must be an integer 0-6, got', dow);
  }
  return PERIOD_BY_DOW[dow];
}

/**
 * A Settings day name to the period the database stores.
 *
 * Case and surrounding spaces are forgiven, because a select's value is display text and display
 * text drifts. The DAY is not forgiven: an unknown name throws.
 *
 * @param {string} name for example `'Friday'`.
 * @returns {string} one of the seven `weekly_*` periods.
 * @throws {TypeError} for a non-string or an unknown day.
 */
export function paydayPeriodFromDayName(name) {
  if (typeof name !== 'string') reject('a weekday name must be a string, got', name);
  const period = PERIOD_BY_NAME[name.trim().toLowerCase()];
  if (!period) reject('unknown weekday name', name);
  return period;
}

/**
 * The period back to a `DayPicker` value.
 *
 * @param {string} period a stored `payday_period`.
 * @returns {number|null} 0-6, or `null` when the family settles up monthly or ad hoc.
 * @throws {TypeError} for a period the database could not hold.
 */
export function dayNumberFromPaydayPeriod(period) {
  if (period === CALENDAR_MONTH || period === AD_HOC) return null;
  const dow = PERIOD_BY_DOW.indexOf(period);
  if (dow === -1) reject('unknown payday period', period);
  return dow;
}

/**
 * The period back to the name the Settings select shows.
 *
 * @param {string} period a stored `payday_period`.
 * @returns {string|null} for example `'Friday'`, or `null` when the family settles up monthly or ad hoc.
 * @throws {TypeError} for a period the database could not hold.
 */
export function dayNameFromPaydayPeriod(period) {
  if (period === CALENDAR_MONTH || period === AD_HOC) return null;
  const name = NAME_BY_PERIOD[period];
  if (!name) reject('unknown payday period', period);
  return name;
}

/**
 * Which kind of payday this family has: `'weekly'`, `'monthly'` or `'ad_hoc'`.
 *
 * It replaced `isWeeklyPayday`, a boolean (#580 I8a). A boolean has one "no" for two different
 * answers, so `isWeeklyPayday(p) ? weekday() : monthly()` would tell an ad hoc family they settle
 * up once a month. Ask this instead, and handle all three.
 *
 * @param {string} period a stored `payday_period`.
 * @returns {'weekly'|'monthly'|'ad_hoc'}
 * @throws {TypeError} for a period the database could not hold.
 */
export function paydayKind(period) {
  if (period === CALENDAR_MONTH) return 'monthly';
  if (period === AD_HOC) return 'ad_hoc';
  // Refuse what the siblings refuse. Answering a kind for `null`, `5`, `'__proto__'` or
  // `'weekly_funday'` would make this unsafe as a guard: every one of those would be routed into
  // some family's branch. Found by qwen3.8-max reviewing 3756669, against the boolean this replaced.
  if (!Object.prototype.hasOwnProperty.call(NAME_BY_PERIOD, period)) reject('unknown payday period', period);
  return 'weekly';
}
